import React, { useEffect, useState } from 'react';

import { Helmet } from 'react-helmet-async';

import API from 'api/API';
import Button from 'components/inputs/basic/Button/Button';
import CenteredSpinner from 'components/layouts/parts/CenteredSpinner/CenteredSpinner';
import Alert from 'components/widgets/alerts/Alert/Alert';

import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

// Make sure to call `loadStripe` outside of a component's render to avoid
// recreating the `Stripe` object on every render.
const STRIPE_PUBLIC_KEY =
  process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'SYSTEM ERROR: SET .env.XXX.REACT_APP_STRIPE_PUBLIC_KEY';
const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

const SetupForm = () => {
  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event: any) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return null;
    }

    const { error } = await stripe.confirmSetup({
      //`Elements` instance that was used to create the Payment Element
      elements,
      confirmParams: {
        return_url: new URL('/payment_status', window.location.origin).toString(),
      },
    });

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      setErrorMessage(error.message || '');
    } else {
      // Payment succeeded, user will be redirected to the return_url
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Alert variant="info" className="my-4">
        <div className="w-full font-semibold">
          Your trial has ended. Enter your credit card details below to continue using Mozart Data.
        </div>
        <div className="w-full text-sm">
          Without a payment method on file, your account will be deactivated after 7 days: ETL will
          pause, warehouse access will be revoked, and your data will be removed from our system.
        </div>
      </Alert>
      <PaymentElement />
      <Alert variant="info" className="mt-4 !bg-pri-gray-100 !text-pri-gray-500 text-sm">
        <div className="w-full">
          By entering my credit card information and selecting "Submit”, I agree to the{' '}
          <a
            href="https://app.mozartdata.com/documents/msa.pdf"
            target="blank"
            rel="noopener noreferrer"
            className="text-sec-blue-light-700"
          >
            Master Services Agreement
          </a>{' '}
          and{' '}
          <a
            href="https://app.mozartdata.com/documents/dpa.pdf"
            target="blank"
            rel="noopener noreferrer"
            className="text-sec-blue-light-700"
          >
            Data Processing Addendum
          </a>{' '}
          governing this contract. I understand that billing will occur in arrears based on usage, at the
          rates specified there.
        </div>
      </Alert>
      {errorMessage && (
        <Alert variant="error" className="mt-4">
          {errorMessage}
        </Alert>
      )}
      <div className="flex justify-end my-2">
        <Button disabled={!stripe} type="submit">
          Submit
        </Button>
      </div>
    </form>
  );
};

const Payment = () => {
  const title = 'Payment Information';
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const api = new API();
    api
      .post('api/create_setup_intent', {})
      .then((response) => {
        if (response.data && response.data.client_secret) {
          setClientSecret(response.data.client_secret);
        } else {
          setError('Failed to retrieve client secret for payment.');
        }
      })
      .catch((err) => {
        if (err.response && err.response.data && err.response.data.error) {
          setError(err.response.data.error);
        } else {
          setError('An unexpected error occurred while creating the setup intent.');
        }
      });
  }, []);

  // If there's an error, display it
  if (error) {
    const errorMessage = <div>{error}</div>;
    return <Alert variant="error" children={errorMessage} />;
  }

  // Ensure clientSecret is set before rendering the PaymentElement
  if (!clientSecret) {
    return <CenteredSpinner containerClass="mt-6" />;
  }

  const options = {
    // passing the SetupIntent's client secret
    clientSecret: clientSecret,
  };

  return (
    <div className="f-center">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={title} />
      </Helmet>
      <div className="mt-4 w-1/2">
        <Elements stripe={stripePromise} options={options}>
          <SetupForm />
        </Elements>
      </div>
    </div>
  );
};

export default Payment;
